import { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Country, countryLanguageConfig, Language } from './locales';
import { detectLanguage, storeLanguage } from './utils';

export type LanguageContext = {
  language: Language;
  changeLanguage: (language: Language) => void;
  languages: Language[];
};

export const CI_MODE_LANGUAGE = 'cimode' as Language;

export const LanguagesContext = createContext<LanguageContext>({
  language: Language.EN,
  languages: [],
  changeLanguage: () => {},
});

type Props = {
  children: ReactNode;
  loader: JSX.Element;
  country: Country;
  ns: Parameters<typeof useTranslation>[0];
};

export function LanguageProvider({ children, loader, ns, country }: Props) {
  const [languages, setLanguages] = useState<Language[]>([]);
  const { ready, i18n } = useTranslation(ns);
  const languageConfig = countryLanguageConfig[country];

  useEffect(() => {
    setLanguages(languageConfig.languages);
    const detectedLanguage = detectLanguage();
    const targetLanguage = detectedLanguage || languageConfig.defaultLanguage;
    i18n.changeLanguage(targetLanguage);
    storeLanguage(targetLanguage);
  }, [i18n, languageConfig.defaultLanguage, languageConfig.languages]);

  const changeLanguage = useCallback(
    (language: Language) => {
      i18n.changeLanguage(language);
      storeLanguage(language);
    },
    [i18n],
  );

  const language = i18n.language as Language;

  if (!ready) {
    return loader; // FIXME: replace with shared after moving client shared to lib
  }

  return (
    <LanguagesContext.Provider
      value={{
        languages,
        language,
        changeLanguage,
      }}
    >
      <Styled.ForceWordBreak breakAll={i18n.language === CI_MODE_LANGUAGE}>{children}</Styled.ForceWordBreak>
    </LanguagesContext.Provider>
  );
}

const Styled = {
  ForceWordBreak: styled.div<{ breakAll: boolean }>`
    ${({ breakAll }) => breakAll && `word-break: break-all;`};
  `,
};
